import React from 'react';
import moment from 'moment';
import styled from 'styled-components';
import { Button, Modal, Radio, Select, Table, Tag, Typography } from 'antd';
import { StopOutlined } from '@ant-design/icons';
import { useMutation, useQuery } from 'react-query';
import { useParams } from 'react-router-dom';

import { ApiClient } from '../../../api';
import { DrawerLayout } from '../../../Layout';
import { PaginationConfig } from '../../../components/common';
import { lpad } from '../../../utils/helper';

const { Title } = Typography;

const blockHourOptions = [1, 3, 6, 12, 24, 48, 72, 168];

const UserLogsWrapper = styled.div`
    .header {
        display: flex;
        align-items: center;
        margin-bottom: 16px;

        .flex-1 {
            flex: 1;
        }
    }

    .summary {
        margin-bottom: 20px;
        padding: 18px;
        background: #ffffff;
        border: 1px solid #eef0f4;
        border-radius: 14px;
        box-shadow: 0 10px 30px rgba(15, 23, 42, 0.06);

        h4.ant-typography {
            margin-bottom: 14px;
        }
    }

    .summary-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 12px;
    }

    .summary-item {
        min-height: 82px;
        padding: 14px;
        background: #f8fafc;
        border: 1px solid #edf1f7;
        border-radius: 10px;
    }

    .summary-label {
        margin-bottom: 8px;
        color: rgba(0, 0, 0, 0.45);
        font-size: 12px;
        font-weight: 600;
        text-transform: uppercase;
    }

    .summary-value {
        color: rgba(0, 0, 0, 0.85);
        font-size: 18px;
        font-weight: 600;
        line-height: 1.35;
        overflow-wrap: anywhere;
    }

    .summary-empty {
        color: rgba(0, 0, 0, 0.45);
    }
`;

const titleize = (value: string) => {
    return value
        .replace(/([A-Z])/g, ' $1')
        .replace(/[_-]/g, ' ')
        .replace(/\s+/g, ' ')
        .trim()
        .replace(/^./, (letter) => letter.toUpperCase());
};

const isDateLike = (value: string) => {
    return /^\d{4}-\d{2}-\d{2}/.test(value) && moment(value).isValid();
};

const renderValue = (value: any) => {
    if (value === null || value === undefined || value === '') {
        return '-';
    }
    if (typeof value === 'boolean') {
        return value ? 'Yes' : 'No';
    }
    if (typeof value === 'string' && isDateLike(value)) {
        return moment(value).format('DD/MM/YYYY hh:mm A');
    }
    if (typeof value === 'object') {
        return JSON.stringify(value);
    }
    return value;
};

const renderEastAfricaTime = (value: string) => {
    if (!value) {
        return '-';
    }
    return moment(value).utcOffset(3).format('DD/MM/YYYY hh:mm A');
};

const renderSummaryValue = (value: any) => {
    if (typeof value === 'number') {
        return Math.floor(value);
    }
    if (typeof value === 'boolean') {
        return <Tag color={value ? 'success' : 'default'}>{renderValue(value)}</Tag>;
    }
    return renderValue(value);
};

const getLogsFromResponse = (response: any) => {
    if (Array.isArray(response?.logs?.data)) {
        return response.logs?.data;
    }
    if (Array.isArray(response?.logs)) {
        return response.logs;
    }
    if (Array.isArray(response?.data)) {
        return response.data;
    }
    return [];
};

const getSummaryFromResponse = (response: any) => {
    return response?.summary || response?.data?.summary || response?.userSummary || response?.data?.userSummary || {};
};

const getTotalFromResponse = (response: any) => {
    return response?.total || response?.logs?.total || response?.data?.total || response?.data?.logs?.total;
};

const columns = [
    {
        title: 'Redeemable',
        dataIndex: 'redeemable',
        render: (val) => `KES ${val ? parseFloat(renderValue(val)).toFixed(2) : '0'}`,
    },
    {
        title: 'Unredeemable',
        dataIndex: 'unredeemable',
        render: (val) => `KES ${val ? parseFloat(renderValue(val)).toFixed(2) : '0'}`,
    },
    {
        title: 'Operation',
        dataIndex: 'op',
        render: (value) => {
            switch (value) {
                case 'Credit':
                    return <Tag color="success">Credit</Tag>;
                case 'Debit':
                    return <Tag color="error">Debit</Tag>;
                default:
                    return renderValue(value);
            }
        },
    },
    {
        title: 'Kind',
        dataIndex: 'kind',
        render: renderValue,
    },
    {
        title: 'Description',
        dataIndex: 'description',
        render: renderValue,
    },
    {
        title: 'Created At',
        dataIndex: 'createdAt',
        render: renderEastAfricaTime,
    },
];

const UserLogsContainer = () => {
    const userId = useParams<'userId'>()?.userId as string;
    const [page, setPage] = React.useState(1);
    const [blockModalVisible, setBlockModalVisible] = React.useState(false);
    const [blockMode, setBlockMode] = React.useState<'permanent' | 'temporary'>('permanent');
    const [blockHours, setBlockHours] = React.useState(24);

    const { data, isFetching, refetch } = useQuery<any>(
        ['userLogsSummary', userId, page],
        ApiClient.fetchUserLogsSummary,
        {
            enabled: Boolean(userId),
            refetchOnWindowFocus: false,
        },
    );

    const blockMutation = useMutation(ApiClient.blockUser, {
        onSuccess: () => {
            ApiClient.showSuccess('User successfully blocked');
            setBlockModalVisible(false);
            refetch();
        },
        onError: (error: any) => {
            ApiClient.showError(error?.response?.data?.message || 'Unable to block user');
        },
    });

    const logs = React.useMemo(() => getLogsFromResponse(data), [data]);
    const summary = React.useMemo(() => getSummaryFromResponse(data), [data]);
    const summaryEntries = React.useMemo(() => {
        return Object.entries(summary || {}).filter(([key]) => key !== 'userId');
    }, [summary]);
    const total = React.useMemo(() => getTotalFromResponse(data), [data]);

    const onBlockConfirm = () => {
        if (blockMode === 'permanent') {
            blockMutation.mutate({ userId, isPermanent: true });
            return;
        }

        blockMutation.mutate({
            userId,
            isPermanent: false,
            blockedTill: moment().add(blockHours, 'hours').toISOString(),
        });
    };

    return (
        <DrawerLayout>
            <UserLogsWrapper>
                <div className="header">
                    <Button
                        danger
                        className="block-button"
                        icon={<StopOutlined />}
                        loading={blockMutation.isLoading}
                        onClick={() => setBlockModalVisible(true)}
                    >
                        Block User
                    </Button>
                </div>

                <Modal
                    title="Block User"
                    visible={blockModalVisible}
                    okText="Block"
                    cancelText="Cancel"
                    confirmLoading={blockMutation.isLoading}
                    okButtonProps={{ danger: true, style: { borderRadius: 14 } }}
                    cancelButtonProps={{ style: { borderRadius: 14 } }}
                    onOk={onBlockConfirm}
                    onCancel={() => setBlockModalVisible(false)}
                >
                    <div className="block-options">
                        <Radio.Group
                            value={blockMode}
                            onChange={(event) => setBlockMode(event.target.value)}
                            optionType="button"
                            buttonStyle="solid"
                        >
                            <Radio.Button value="permanent">Permanent</Radio.Button>
                            <Radio.Button value="temporary">Temporary</Radio.Button>
                        </Radio.Group>

                        {blockMode === 'temporary' ? (
                            <div className="block-duration">
                                <div className="block-duration-label">Duration</div>
                                <Select value={blockHours} onChange={setBlockHours}>
                                    {blockHourOptions.map((hours) => (
                                        <Select.Option value={hours} key={hours}>
                                            {hours} {hours === 1 ? 'hour' : 'hours'}
                                        </Select.Option>
                                    ))}
                                </Select>
                            </div>
                        ) : null}
                    </div>
                </Modal>

                <div className="summary">
                    <Title level={4}>User Summary</Title>
                    {summaryEntries.length ? (
                        <div className="summary-grid">
                            {summaryEntries.map(([key, value]) => (
                                <div className="summary-item" key={key}>
                                    <div className="summary-label">{titleize(key)}</div>
                                    <div className="summary-value">{renderSummaryValue(value)}</div>
                                </div>
                            ))}
                        </div>
                    ) : (
                        <div className="summary-empty">No summary available</div>
                    )}
                </div>

                <Title level={4}>Logs</Title>
                <Table
                    loading={isFetching}
                    rowKey={(_, index) => `${page}-${index}`}
                    columns={columns}
                    dataSource={logs}
                    locale={{ emptyText: 'No Logs' }}
                    pagination={
                        total
                            ? {
                                  ...PaginationConfig,
                                  total,
                                  onChange: setPage,
                              }
                            : false
                    }
                />
            </UserLogsWrapper>
        </DrawerLayout>
    );
};

export default UserLogsContainer;
